import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  Platform,
  RefreshControl,
} from 'react-native';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import { getCurrencyDisplayFormat, s, fs, v } from '../../utils/libs';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  setSummaryLoading,
  setSummaryError,
  setSummaryData,
  setAnnualLoading,
  setAnnualError,
  setAnnualPoints,
} from '../../redux/backoffice/expensesSlice';
import {
  getMonthlyExpenses,
  getAnnualExpenses,
} from '../../api/backofficeExpenses';
import type { ExpenseSummary } from '../../api/backofficeExpenses';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import ExpenseHeroCard from '../../components/backoffice/expenses/ExpenseHeroCard';
import ExpensePaceCard from '../../components/backoffice/expenses/ExpensePaceCard';
import ExpenseCategoryCard from '../../components/backoffice/expenses/ExpenseCategoryCard';
import AnnualExpensesBarChart, {
  AnnualExpensesLegend,
} from '../../components/charts/AnnualExpensesBarChart';
import { getDashboardComparisonPeriodLabel } from '../../utils/dashboardPeriods';
import { getExpenseCategoryShares } from '../../utils/expenseCategories';
import { getSpendingPace } from '../../utils/expensePace';
import moment from 'moment';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';

// ─── Helpers ─────────────────────────────────────────────────────────────

type DateRange = {
  start: string;
  end: string;
};

const getDateRangeForSelection = (selection: string): DateRange => {
  switch (selection) {
    case 'Today':
      return {
        start: moment().startOf('day').toISOString(),
        end: moment().endOf('day').toISOString(),
      };
    case 'Yesterday':
      return {
        start: moment().subtract(1, 'day').startOf('day').toISOString(),
        end: moment().subtract(1, 'day').endOf('day').toISOString(),
      };
    case 'Weekly':
      return {
        start: moment().startOf('week').toISOString(),
        end: moment().endOf('week').toISOString(),
      };
    case 'Monthly':
      return {
        start: moment().startOf('month').toISOString(),
        end: moment().endOf('month').toISOString(),
      };
    case 'Yearly':
      return {
        start: moment().startOf('year').toISOString(),
        end: moment().endOf('year').toISOString(),
      };
    default:
      return {
        start: moment().startOf('day').toISOString(),
        end: moment().endOf('day').toISOString(),
      };
  }
};

const getDateRangeLabel = (selection: string): string => {
  switch (selection) {
    case 'Today':
      return moment().format('MMMM D, YYYY');
    case 'Yesterday':
      return moment().subtract(1, 'day').format('MMMM D, YYYY');
    case 'Weekly':
      return `${moment().startOf('week').format('MMM D')} – ${moment().endOf('week').format('MMM D, YYYY')}`;
    case 'Monthly':
      return moment().format('MMMM YYYY');
    case 'Yearly':
      return String(moment().year());
    default:
      return moment().format('MMMM D, YYYY');
  }
};

// ─── Screen ──────────────────────────────────────────────────────────────

const BackOfficeExpensesScreen: React.FC = () => {
  const { theme } = useTheme();
  const { useSidebarNav } = useLayoutSize();
  const styles = useMemo(
    () => createStyles(theme, useSidebarNav),
    [theme, useSidebarNav],
  );
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const employee = useAppSelector(
    st => st.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(st => st.location.locationVersion);
  const currency = useAppSelector(
    st =>
      st.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );
  const year = new Date().getFullYear();

  const summary = useAppSelector(
    st => st.backofficeExpenses.summary ?? { data: null, loading: false, error: null },
  );
  const annual = useAppSelector(
    st => st.backofficeExpenses.annual ?? { points: [], loading: false, error: null },
  );

  const [selectedRange, setSelectedRange] = useState<
    'Today' | 'Yesterday' | 'Weekly' | 'Monthly' | 'Yearly'
  >('Monthly');
  // The dashboard and Sales cards pass their period so this screen opens on
  // the same totals that were tapped. The focus refetch uses it straight away
  // (so the old period is never loaded first), then it becomes the screen's
  // own selection and the param is dropped so the dropdown stays in charge.
  const route = useRoute<any>();
  const rangeParam: typeof selectedRange | undefined = route.params?.range;
  const activeRange = rangeParam ?? selectedRange;
  useEffect(() => {
    if (!rangeParam) return;
    setSelectedRange(rangeParam);
    (navigation as any).setParams({ range: undefined });
  }, [rangeParam, navigation]);
  const [selectedDonutIndex, setSelectedDonutIndex] = useState<number | null>(
    null,
  );
  const [refreshing, setRefreshing] = useState(false);

  const categoryRows = useMemo(
    () => getExpenseCategoryShares(summary.data?.totalsByType),
    [summary.data?.totalsByType],
  );

  const fetchSummary = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    try {
      dispatch(setSummaryLoading(true));
      dispatch(setSummaryError(null));
      const { start, end } = getDateRangeForSelection(activeRange);
      const res = await getMonthlyExpenses(
        axiosBackoffice as any,
        ownerId,
        { accessToken: owner?.accessToken },
        { startISO: start, endISO: end },
      );
      if (res?.status === 200) {
        const data = res?.data?.data as ExpenseSummary;
        dispatch(setSummaryData(data ?? null));
      } else {
        dispatch(
          setSummaryError(
            String(res?.data?.message || 'Failed to load expenses summary'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(
        setSummaryError(e?.message || 'Error fetching expenses summary'),
      );
    } finally {
      dispatch(setSummaryLoading(false));
    }
  }, [owner?.accessToken, ownerId, dispatch, axiosBackoffice, activeRange]);

  const fetchAnnual = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    try {
      dispatch(setAnnualLoading(true));
      dispatch(setAnnualError(null));
      const res = await getAnnualExpenses(
        axiosBackoffice as any,
        ownerId,
        year,
        {
          accessToken: owner?.accessToken,
        },
      );
      if (res?.status === 200) {
        const payload = (res?.data?.data || res?.data || []) as any[];
        const MONTHS = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];

        const byMonth: Array<{
          month: string;
          totalsByType: Record<string, number>;
          total: number;
        }> = MONTHS.map(m => ({ month: m, totalsByType: {}, total: 0 }));

        (payload || []).forEach((entry: any, index: number) => {
          let monthIndex = index;
          const maybeMonth = Number(entry?.month);
          if (
            !Number.isNaN(maybeMonth) &&
            maybeMonth >= 1 &&
            maybeMonth <= 12
          ) {
            monthIndex = maybeMonth - 1;
          }

          const expenses = (entry?.expenses as any) || entry || {};
          const totalsByType =
            (expenses?.totalsByType as Record<string, number>) || {};

          const fallbackMap: Record<string, number> = {};
          if (Object.keys(totalsByType || {}).length === 0) {
            if (typeof expenses?.payments !== 'undefined') {
              fallbackMap.payments = Number(expenses?.payments || 0);
            }
            if (typeof expenses?.stocks !== 'undefined') {
              fallbackMap.stocks = Number(expenses?.stocks || 0);
            }
          }

          const map: Record<string, number> =
            Object.keys(totalsByType || {}).length > 0
              ? totalsByType
              : fallbackMap;

          const totalFromMap = Object.values(map || {}).reduce(
            (acc, v) => acc + Number(v || 0),
            0,
          );
          const total = Number(expenses?.totalExpenses || totalFromMap);

          if (monthIndex >= 0 && monthIndex < 12) {
            byMonth[monthIndex] = {
              month: MONTHS[monthIndex],
              totalsByType: map,
              total,
            };
          }
        });

        dispatch(setAnnualPoints(byMonth));
      } else {
        dispatch(
          setAnnualError(
            String(res?.data?.message || 'Failed to load annual expenses'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setAnnualError(e?.message || 'Error fetching annual expenses'));
    } finally {
      dispatch(setAnnualLoading(false));
    }
  }, [owner?.accessToken, ownerId, dispatch, axiosBackoffice, year]);

  useFocusEffect(
    useCallback(() => {
      fetchSummary();
      fetchAnnual();
    }, [fetchSummary, fetchAnnual, locationVersion]),
  );

  const onRefresh = useCallback(async () => {
    if (refreshing || summary.loading) return;
    setRefreshing(true);
    try {
      await Promise.all([fetchSummary(), fetchAnnual()]);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, summary.loading, fetchSummary, fetchAnnual]);

  const headerRightNode = useMemo(
    () => (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginRight: s(8),
        }}>
        <View style={{ marginLeft: Platform.OS === 'web' ? s(8) : 0 }}>
          <DateRangeDropdown
            variant="compact"
            chipBgColor={'rgba(255,255,255,0.15)'}
            chipTextColor={theme.colors.textInverse}
            chipBorderColor={'rgba(255,255,255,0.35)'}
            selectedOverride={selectedRange}
            onChange={val => setSelectedRange(val)}
          />
        </View>
      </View>
    ),
    [selectedRange, theme.colors.textInverse],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => headerRightNode,
    });
  }, [navigation, headerRightNode]);

  const webHeaderNode = useMemo(
    () => (
      <WebHeaderBar
        title={<BackOfficeHeaderLocationPicker />}
        titleAlign="center"
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            (navigation as any).navigate('Dashboard');
          } catch {
            if (navigation.canGoBack()) navigation.goBack();
          }
        }}
      />
    ),
    [headerRightNode, navigation],
  );
  useWebHeader(webHeaderNode, true);

  // A failed load shows "—" with Retry, never zeros or the last period's figures.
  const summaryError = summary.loading ? null : summary.error;
  const firstLoad = summary.loading && !summary.data;

  // Pace is for the month in progress only, as before.
  const pace = useMemo(() => {
    if (selectedRange !== 'Monthly' || !summary.data) return null;
    const { start } = getDateRangeForSelection(selectedRange);
    return getSpendingPace({
      totalExpenses: summary.data.totalExpenses,
      previousTotal: summary.data.previousPeriod?.totalExpenses || 0,
      monthStart: moment(start),
      now: moment(),
    });
  }, [summary.data, selectedRange]);

  const heroCard = (
    <ExpenseHeroCard
      summary={summary.data}
      loading={firstLoad}
      error={summaryError}
      dateRangeLabel={getDateRangeLabel(selectedRange)}
      currencyText={currencyText}
      comparisonPeriodLabel={getDashboardComparisonPeriodLabel(selectedRange)}
      onViewExpenses={() => navigation.navigate('BackOfficeLatestExpenses')}
      onAddExpense={() => navigation.navigate('BackOfficeExpenseCategories')}
      onRetry={() => {
        fetchSummary();
      }}
    />
  );

  const paceCard =
    pace && !summaryError ? (
      <ExpensePaceCard
        pace={pace}
        currencyText={currencyText}
        monthLabel={moment().format('MMMM')}
      />
    ) : null;

  const categoryCard = (
    <ExpenseCategoryCard
      rows={categoryRows}
      currencyText={currencyText}
      loading={firstLoad}
      failed={Boolean(summaryError)}
      selectedIndex={selectedDonutIndex}
      onSelect={index =>
        setSelectedDonutIndex(prev => (prev === index ? null : index))
      }
      style={useSidebarNav ? styles.fill : undefined}
    />
  );

  const yearlyCard = (
    <View style={styles.card}>
      <View style={styles.yearlyHeader}>
        <View style={styles.yearlyTitleRow}>
          <Text style={styles.cardTitle}>Yearly Expenses</Text>
          <Text style={styles.cardCaption}>{year}</Text>
        </View>
        <AnnualExpensesLegend data={annual.points} />
      </View>
      {annual.loading && annual.points.length === 0 ? (
        <View style={styles.chartPlaceholder} />
      ) : annual.error && annual.points.length === 0 ? (
        <Text style={styles.chartMessage}>
          Expenses could not be loaded right now
        </Text>
      ) : (
        <AnnualExpensesBarChart
          data={annual.points}
          currentMonthIndex={new Date().getMonth()}
        />
      )}
    </View>
  );

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }>
      {/* Sidebar layout (web / tablet): figures left, categories right. */}
      {useSidebarNav ? (
        <View style={styles.columns}>
          <View style={styles.column}>
            {heroCard}
            {paceCard}
          </View>
          <View style={styles.column}>{categoryCard}</View>
        </View>
      ) : (
        <>
          {heroCard}
          {paceCard}
          {categoryCard}
        </>
      )}
      {yearlyCard}
    </ScrollView>
  );
};

export default BackOfficeExpensesScreen;

const createStyles = (theme: any, useSidebarNav: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    // Each section is its own card on the grey canvas, as on Customer and
    // Product Details. Side gutter matches the Dashboard cards (s(10)).
    content: {
      paddingHorizontal: s(10),
      paddingTop: s(12),
      rowGap: v(12),
      paddingBottom: v(32),
      ...(Platform.OS === 'web' && {
        paddingTop: s(24),
      }),
    },
    columns: {
      flexDirection: 'row',
      alignItems: 'stretch',
      columnGap: s(16),
    },
    column: {
      flex: 1,
      minWidth: 0,
      rowGap: v(12),
    },
    fill: {
      flex: 1,
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(16),
      paddingTop: v(14),
      paddingBottom: v(8),
      ...theme.shadows.sm,
    },
    // Phones: title and year, then the key. Sidebar: the key at the right.
    yearlyHeader: useSidebarNav
      ? {
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: s(16),
          rowGap: v(8),
        }
      : {
          rowGap: v(10),
        },
    yearlyTitleRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: useSidebarNav ? 'flex-start' : 'space-between',
      columnGap: s(8),
    },
    cardTitle: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    cardCaption: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    chartPlaceholder: {
      height: v(200),
      marginTop: v(12),
      marginBottom: v(8),
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    chartMessage: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      textAlign: 'center',
      paddingVertical: v(24),
    },
  });
